/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import ApplicationFeatures from './ApplicationFeatures';

/**
 * Defines the supported Feature Flag values which are used as the values of
 * the feature flag names (keys).
 *
 * ```typescript
 *    _features = {
 *      // feature key ...          // feature value ...
 *     [FeatureFlag.SOME_PAGE]      : 'some-application-id',
 *     [FeatureFlag.SOME_COMPONENT] : true
 *   }
 * ```
 */
export type FeatureFlagEntry = [string, boolean];

/**
 * The `ApplicationFeatureFlags` class provides the base class implementation for integrating
 * Feature Flags within an application. Concrete implementations (project specific) can extend
 * this class to utilize the underlying functionality by simply defining a map of feature flags
 * via the `_features` member:
 *
 * ```typescript
 *   // ion/packages/some-app/src/security/Feature.ts
 *   export const SOME_APPLICATION_PAGE = '5884fe57-56d8-e446-c972-31a3fdbac40f'
 *
 *   // ion/packages/some-app/src/security/FeatureFlags.ts
 *   import ApplicationFeatureFlags from '@ion/core/src/security/ApplicationFeatureFlags';
 *   import {SOME_APPLICATION_PAGE} from './Features';
 *
 *   // define application aware feature flags ...
 *   export enum FeatureFlag {
 *     SOME_PAGE      = 'some-page',
 *     SOME_COMPONENT = 'some-ui-component'
 *   }
 *
 *   // implement application specific feature flags ...
 *   class FeatureFlags extends ApplicationFeatureFlags {
 *      // implement the _feature mapping for supported features
 *      _features = {
 *       // define a feature flag for an application specific role feature ...
 *       [FeatureFlag.SOME_PAGE]      : SOME_APPLICATION_PAGE,
 *       // define a ui only feature (one that is not related to role features) ...
 *       [FeatureFlag.SOME_COMPONENT] : true
 *     }
 *   };
 *
 *   // export singleton instance of FeatureFlags implementation ...
 *   export default new FeatureFlags();
 *
 * ```
 *
 * @see {@link ApplicationFeatures}
 */
export default class ApplicationFeatureFlags {
  /**
   * Provides mapping of pre-defined feature flag constants as keys where each key
   * represents a supported feature flag (which, for example, can be used in the
   * query string, or via a build argument, etc.).
   *
   * While a Feature Flag's keys and values can be arbitrarily defined, generally,
   * their values are either:
   *
   *   - A Role Feature Id (guid): (either seeded and / or unseeded)
   *   - A boolean value of true (for non-Role based Feature Flags). Note the value
   *     of true simply indicates that the given feature flag is supported.
   *
   * The following examples demonstrates two pre-defined feature flags, the first
   * `SOME_PAGE` mapping to a Role Feature's GUID, and the second `SOME_COMPONENT`
   * having a value of `true`, which simply indicates that the feature flag is
   * supported (but is unrelated to Role Features):
   *
   * ```typescript
   *   // ion/packages/some-app/src/security/FeatureFlags.ts
   *
   *   // import role feature guid supported by feature flags ...
   *   import {SOME_APPLICATION_PAGE} from './Features';
   *
   *   export enum FeatureFlag {
   *     SOME_PAGE      = 'some-page',
   *     SOME_COMPONENT = 'some-ui-component'
   *   }
   *
   *   class FeatureFlags extends ApplicationFeatureFlags {
   *      _features = {
   *       [FeatureFlag.SOME_PAGE]      : SOME_APPLICATION_PAGE,
   *       [FeatureFlag.SOME_COMPONENT] : true
   *     }
   *   }
   * ```
   */
  protected _features = {};

  /**
   * The `_enabled` member defines the definitive list of supported Feature Flags
   * which have been enabled. Enablement of Feature Flags can be facilitated via
   * any number of mechanisms independent of the underlying Feature Flags API.
   * The API itself simply provides a means of defining and working with Feature
   * Flags, not when or how they are enabled or disabled.
   *
   * The following examples demonstrates two pre-defined feature flags, the first
   * `SOME_PAGE` mapping to a Role Feature's GUID, and the second `SOME_COMPONENT`
   * having a value of `true`, which simply indicates that the feature flag is
   *  supported (but is unrelated to Role Features). By default, all Feature Flags
   * are disabled, and their enabled state is only activated via a call to the
   * `applyFeatureFlags` method, whereby the feature flags passed to the method
   * determine which pre-defined feature flags are enabled.
   *
   * ```typescript
   *   // ion/packages/some-app/src/security/FeatureFlags.ts
   *
   *   // import role feature guid supported by feature flags ...
   *   import {SOME_APPLICATION_PAGE} from './Features';
   *
   *   export enum FeatureFlag {
   *     SOME_PAGE      = 'some-page',
   *     SOME_COMPONENT = 'some-ui-component'
   *   }
   *
   *   class FeatureFlags extends ApplicationFeatureFlags {
   *      _features = {
   *       [FeatureFlag.SOME_PAGE]      : SOME_APPLICATION_PAGE,
   *       [FeatureFlag.SOME_COMPONENT] : true
   *     }
   *   }
   *
   *   ...
   *   // ion/packages/some-app/src/actions/initialization.ts
   *   const featureFlags = SomeAPI.getFeatureFlags(); // 'some-page';
   *   FeatureFlags.applyFeatureFlags(featureFlags);
   *
   *   FeatureFlags.isEnabled(FeatureFlag.SOME_PAGE); // true
   *   FeatureFlags.isEnabled(FeatureFlag.SOME_COMPONENT); // false
   * ```
   */
  protected _enabled = {};

  /**
   * Provides a convenience for determining if a given Feature Flag is supported.
   * The notion of supported simply indicates that the application has predefined
   * the given feature flag in it's list of supported feature flags. This method
   * is primarily provided as a safe-guard for determining if an applications
   * Feature Flag enumeration exists as a predefined feature; that is, in the event
   * a Feature Flag enumeration is removed from the list of pre-defined features,
   * but the enumeration value still exists. Generally, this should be avoided by
   * ensuring an application's Feature Flag enumerations do not contain any values
   * which are not currently added to the list of feature flags.
   *
   * ```typescript
   *   export enum FeatureFlag {
   *     SOME_PAGE            = 'some-page',
   *     SOME_COMPONENT       = 'some-ui-component',
   *     SOME_OTHER_COMPONENT = 'some-other-ui-component'
   *   }
   *
   *   class FeatureFlags extends ApplicationFeatureFlags {
   *      _features = {
   *       [FeatureFlag.SOME_PAGE]            : SOME_APPLICATION_PAGE,
   *       [FeatureFlag.SOME_OTHER_COMPONENT] : true
   *     }
   *   }
   *   ...
   *
   *   FeatureFlags.hasFeatureFlag(FeatureFlag.SOME_PAGE); // true
   *   FeatureFlags.hasFeatureFlag(FeatureFlag.SOME_PAGE); // false
   *   FeatureFlags.hasFeatureFlag(FeatureFlag.SOME_OTHER_COMPONENT); // false
   * ```
   *
   * @param  feature The name of the pre-defined Feature from which to determine if
   *         it is `supported`; i.e. it has been added to the list of predefined
   *         feature flag entries.
   * @return `true` if the given feature is supported, otherwise, `false`.
   */
  public hasFeatureFlag(feature: string): boolean {
    return !!this._features[feature];
  };

  /**
   * Provides a convenience for determining if a given Feature Flag is `enabled`.
   *
   * Concrete implementations are encouraged to overload this method by defining
   * an application specific `FeatureFlag` enumeration, and requiring the enumeration
   * as an argument, and then simply delegating to this method:
   *
   * ```typescript
   *
   *   // define application aware feature flags ...
   *   export enum FeatureFlag {
   *     SOME_PAGE      = 'some-page',
   *     SOME_COMPONENT = 'some-ui-component'
   *   }
   *
   *   class SomeApplicationFeatureFlags extends ApplicationFeatureFlags {
   *     // overload isEnabled and use FeatureFlag enum as argument
   *     public isEnabled(feature: FeatureFlag): boolean {
   *       return super.isEnabled(feature as string);
   *     }
   *   }
   * ```
   *
   * @param  feature The name of the pre-defined Feature from which to determine if
   *         it is `enabled`.
   * @return `true` if the given `feature` is enabled, otherwise, `false`.
   */
  public isEnabled(feature: string): boolean {
    return Boolean(this._enabled[feature]);
  }

  /**
   * Provides a convenience for determining if a given Feature Flag is `disabled`.
   *
   * Concrete implementations are encouraged to overload this method by defining
   * an application specific `FeatureFlag` enumeration, and requiring the enumeration
   * as an argument, and then simply delegating to this method:
   *
   * ```typescript
   *
   *   // define application aware feature flags ...
   *   export enum FeatureFlag {
   *     SOME_PAGE      = 'some-page',
   *     SOME_COMPONENT = 'some-ui-component'
   *   }
   *
   *   class SomeApplicationFeatureFlags extends ApplicationFeatureFlags {
   *     // overload isDisabled and use FeatureFlag enum as argument
   *     public isDisabled(feature: FeatureFlag): boolean {
   *       return super.isDisabled(feature as string);
   *     }
   *   }
   * ```
   * @param  feature The name of the pre-defined Feature from which to determine if
   *         it is `disabled`.
   * @return `true` if the given `feature` is `disabled`, otherwise, `false`.
   */
  public isDisabled(feature: string): boolean {
    return !this.isEnabled(feature);
  }

  /**
   * Provides the single point of access for enabling an application's Feature Flags.
   * This method accepts an Array of Feature Flag keys (those which have been predefined
   * in the `_features` map) and sets the underlying enabled state for each Feature Flag
   * to `true`.
   *
   * ```typescript
   *   // ion/packages/some-app/src/security/FeatureFlags.ts
   *
   *   // import role feature guid supported by feature flags ...
   *   import {SOME_APPLICATION_PAGE} from './Features';
   *
   *   export enum FeatureFlag {
   *     SOME_PAGE      = 'some-page',
   *     SOME_COMPONENT = 'some-ui-component'
   *   }
   *
   *   class FeatureFlags extends ApplicationFeatureFlags {
   *      _features = {
   *       [FeatureFlag.SOME_PAGE]      : SOME_APPLICATION_PAGE,
   *       [FeatureFlag.SOME_COMPONENT] : true
   *     }
   *   }
   *
   *   ...
   *   // ion/packages/some-app/src/actions/initialization.ts
   *   const featureFlags = SomeAPI.getFeatureFlags(); // ['some-page', 'unsupported'];
   *
   *   // only 'some-page' is returned since it is supported, and 'unsupported' is not
   *   // returned because it hasn't been defined in the `_features` mappings ...
   *   FeatureFlags.applyFeatureFlags(featureFlags); // ['some-page']
   * ```
   *
   * @param   featureFlags A list of Feature Flags which are to be enabled, the provided
   *          feature flags must match the keys of those which have been predefined
   *          in the `_features` mapping.
   * @returns An array of all enabled feature flags. This value can be used to further
   *          determine if the provided feature flags that we're passed in are supported,
   *          if needed.
   */
  public applyFeatureFlags(flags: string[]): string[] {
    if (Array.isArray(flags)) {
      flags?.forEach((flag: string) => {
        if (this._features[flag]) {
          this._enabled[flag] = true;
        }
      });
    }
    return Object.keys(this._enabled);
  };

  /**
   * Provides the single point of access for retrieving an Application's enabled Role
   * Feature Ids (GUIDs).
   *
   * ```typescript
   *   // ion/packages/some-app/src/security/Feature.ts
   *   export const SOME_APPLICATION_PAGE = '5884fe57-56d8-e446-c972-31a3fdbac40f'
   *
   *   ...
   *
   *   // ion/packages/some-app/src/security/FeatureFlags.ts
   *   import {SOME_APPLICATION_PAGE} from './Features';
   *
   *   export enum FeatureFlag {
   *     SOME_PAGE      = 'some-page',
   *     SOME_COMPONENT = 'some-ui-component'
   *   }
   *
   *   class FeatureFlags extends ApplicationFeatureFlags {
   *      _features = {
   *       [FeatureFlag.SOME_PAGE]      : SOME_APPLICATION_PAGE,
   *       [FeatureFlag.SOME_COMPONENT] : true
   *     }
   *   }
   *
   *   ...
   *
   *   const featureFlags = SomeAPI.getFeatureFlags(); // ['some-page', 'some-ui-component'];
   *   FeatureFlags.applyFeatureFlags(featureFlags);
   *   FeatureFlags.getRoleFeatures(); // '5884fe57-56d8-e446-c972-31a3fdbac40f'
   *
   * ```
   *
   * @returns An array of all pre-defined Feature Flags which map to Role Features and
   *          have been enabled.
   */
  public getRoleFeatures(): string[] {
    return Object.entries(this._enabled).filter(([key]) => (
      ApplicationFeatures.isFeatureId(this._features[key])
    )).map(([key]) => this._features[key]);
  };

  /**
   * Provides the single point of access for retrieving an Application's enabled non
   * Role Based Feature Flags. This method is essentially the inverse operation of the
   * `getRoleFeatures` method.
   *
   * ```typescript
   *   // ion/packages/some-app/src/security/Feature.ts
   *   export const SOME_APPLICATION_PAGE = '5884fe57-56d8-e446-c972-31a3fdbac40f'
   *
   *   ...
   *
   *   // ion/packages/some-app/src/security/FeatureFlags.ts
   *   import {SOME_APPLICATION_PAGE} from './Features';
   *
   *   export enum FeatureFlag {
   *     SOME_PAGE      = 'some-page',
   *     SOME_COMPONENT = 'some-ui-component'
   *   }
   *
   *   class FeatureFlags extends ApplicationFeatureFlags {
   *      _features = {
   *       [FeatureFlag.SOME_PAGE]      : SOME_APPLICATION_PAGE,
   *       [FeatureFlag.SOME_COMPONENT] : true
   *     }
   *   }
   *
   *   ...
   *
   *   const featureFlags = SomeAPI.getFeatureFlags(); // ['some-page', 'some-ui-component'];
   *   FeatureFlags.applyFeatureFlags(featureFlags);
   *   FeatureFlags.getNonRoleFeatures(); // 'some-ui-component'
   *
   * ```
   *
   * @returns An array of all pre-defined Feature Flags which map to non Role based Features
   *          and have been enabled.
   */
  public getNonRoleFeatures(): string[] {
    return Object.entries(this._enabled).filter(([key]) => (
      !ApplicationFeatures.isFeatureId(this._features[key])
    )).map(([key]) => key);
  }

  /**
   * The `reset` method allows for resetting the state of all applied feature flags;
   * that is, setting the Feature Flags API back to it's initial state prior to the
   * enabling of any Feature Flags via the `applyFeatureFlags` method.
   *
   * This method should be used with caution and is provided for specific use-cases
   * where a Feature Flag implementation should be reset back to it's initial state,
   * such as if a users current Group changes, etc.
   */
  public _reset_() {
    this._enabled = {};
  }
};
